import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertCircle, ArrowLeft, Mail } from 'lucide-react';
import type { AuthProviderFormProps } from '@reflex/plugin-api';
import { Button } from '@reflex/ui/components/ui/button';
import { Input } from '@reflex/ui/components/ui/input';
import { Label } from '@reflex/ui/components/ui/label';
import { emailLogin, emailRegister, getEmailRegistrationStatus } from './email-auth-api';
import { describeEmailAuthError } from './describeEmailAuthError';

interface EmailPasswordValues {
  email: string;
  password: string;
  name?: string;
}

const loginSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
});

const registerSchema = z
  .object({
    email: z.string().min(1, 'Email is required').email('Enter a valid email address'),
    name: z.string().trim().max(120).optional(),
    password: z.string().min(8, 'Password must be at least 8 characters'),
    confirmPassword: z.string().min(1, 'Confirm your password'),
  })
  .refine((v) => v.password === v.confirmPassword, {
    path: ['confirmPassword'],
    message: 'Passwords do not match',
  });

type LoginValues = z.infer<typeof loginSchema>;
type RegisterValues = z.infer<typeof registerSchema>;

/**
 * Self-contained email/password sign-in & registration surface contributed by
 * the `auth-email` plugin and rendered by the host through the auth-provider
 * form slot ({@link AuthProviderFormProps}). It owns everything email-specific:
 * the RHF + Zod fields, the submit -> API -> token -> `onSuccess` flow, error
 * display, the operator-gated registration-status check, and the
 * reveal-button / back affordance used when OAuth providers are shown
 * alongside it. The host only coordinates surrounding layout via
 * `onExpandedChange`.
 *
 * One surface serves both jobs: it opens on sign-in and, when the operator
 * allows self-service registration, offers an inline switch to the register
 * form. There is no host-supplied mode — the screen that renders this form
 * is the same for returning and brand-new users.
 */
export function EmailAuthForm({
  hasOtherProviders,
  onSuccess,
  onExpandedChange,
}: AuthProviderFormProps) {
  // With OAuth siblings the form hides behind a reveal button; as the only
  // option it renders inline from the start.
  const [revealed, setRevealed] = useState(!hasOtherProviders);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [surface, setSurface] = useState<'login' | 'register'>('login');
  // Carried across the login/register switch so a visitor who typed their
  // address, then discovered they have no account, doesn't retype it. The
  // two surfaces are separate components with their own form state, so
  // this has to live above them.
  const [email, setEmail] = useState('');

  // Registration is operator-gated, and the status decides whether to offer
  // the "create an account" switch at all. Operator config doesn't change
  // mid-session, so fetch it once rather than on every mount of the screen.
  const registrationQuery = useQuery({
    queryKey: ['email-auth', 'registration'],
    queryFn: getEmailRegistrationStatus,
    staleTime: Infinity,
  });
  // Default to hidden while loading (avoids a flash if registration is
  // disabled). Default to visible on error: a failed status check means we
  // can't confirm registration is off, so suppressing the toggle silently
  // locks out new users. The registration API itself will reject if needed.
  const registrationAllowed = registrationQuery.data?.enabled ?? registrationQuery.isError;

  const mutation = useMutation({
    mutationFn: (values: EmailPasswordValues) =>
      surface === 'register'
        ? emailRegister({ email: values.email, password: values.password, name: values.name })
        : emailLogin(values.email, values.password),
    onSuccess: () => {
      void onSuccess();
    },
    onError: (err: unknown) => setErrorMessage(describeEmailAuthError(err)),
  });

  // The form "takes over" the card (host hides OAuth + dev affordances) only
  // when it is revealed AND there are siblings to hide. As the sole provider
  // it renders inline without ever claiming the card, so the host keeps
  // showing whatever else it has.
  const expanded = revealed && hasOtherProviders;
  useEffect(() => {
    onExpandedChange?.(expanded);
  }, [expanded, onExpandedChange]);

  function submit(values: EmailPasswordValues) {
    setErrorMessage(null);
    mutation.mutate(values);
  }

  // Collapsed: a reveal button plus the "or" divider before the OAuth buttons.
  if (hasOtherProviders && !revealed) {
    return (
      <div className="flex flex-col gap-3">
        <Button
          variant="outline"
          size="lg"
          className="w-full justify-center"
          onClick={() => setRevealed(true)}
          data-testid="email-auth-show-form"
        >
          <Mail className="size-5" />
          Continue with email
        </Button>
        <OrDivider />
      </div>
    );
  }

  // Expanded / inline: the full form, with a back affordance when it can collapse.
  return (
    <div className="flex flex-col gap-3">
      {hasOtherProviders && (
        <button
          type="button"
          onClick={() => {
            setRevealed(false);
            setSurface('login');
            setErrorMessage(null);
            setEmail('');
          }}
          data-testid="email-auth-back"
          className="-ml-1 flex w-fit items-center gap-1 rounded-md px-1 py-0.5 text-sm text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <ArrowLeft className="icon-sm" />
          Back to all options
        </button>
      )}
      {surface === 'register' ? (
        <RegisterForm
          onSubmit={submit}
          isSubmitting={mutation.isPending}
          errorMessage={errorMessage}
          defaultEmail={email}
          onEmailChange={setEmail}
        />
      ) : (
        <LoginForm
          onSubmit={submit}
          isSubmitting={mutation.isPending}
          errorMessage={errorMessage}
          defaultEmail={email}
          onEmailChange={setEmail}
        />
      )}
      {(surface === 'register' || registrationAllowed) && (
        <button
          type="button"
          onClick={() => {
            setSurface(surface === 'login' ? 'register' : 'login');
            setErrorMessage(null);
          }}
          data-testid="email-auth-unified-switch"
          className="text-center text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          {surface === 'login' ? (
            <>
              New to Reflex? <span className="font-medium underline">Create an account</span>
            </>
          ) : (
            <>
              Already have an account? <span className="font-medium underline">Sign in</span>
            </>
          )}
        </button>
      )}
    </div>
  );
}

/** Visual "or" separator placed between the email surface and OAuth buttons. */
function OrDivider() {
  return (
    <div className="flex items-center gap-3 py-0.5 text-2xs uppercase tracking-wide text-muted-foreground">
      <span className="h-px flex-1 bg-border" />
      or
      <span className="h-px flex-1 bg-border" />
    </div>
  );
}

interface FormProps {
  onSubmit: (values: EmailPasswordValues) => void;
  isSubmitting: boolean;
  errorMessage?: string | null;
  /** Seeds the email field so it survives the login/register switch. */
  defaultEmail: string;
  /** Lifts each keystroke so the sibling surface can be seeded with it. */
  onEmailChange: (email: string) => void;
}

function FormError({ message }: { message?: string | null }) {
  if (!message) return null;
  return (
    <div
      role="alert"
      data-testid="email-auth-error"
      className="flex items-center gap-2 rounded-md border border-destructive/25 bg-destructive/[0.07] px-3 py-2 text-sm text-destructive"
    >
      <AlertCircle className="size-4 shrink-0" />
      <span>{message}</span>
    </div>
  );
}

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return <p className="text-xs text-destructive">{message}</p>;
}

function LoginForm({
  onSubmit,
  isSubmitting,
  errorMessage,
  defaultEmail,
  onEmailChange,
}: FormProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: defaultEmail, password: '' },
  });
  const emailField = register('email');

  return (
    <form
      className="flex flex-col gap-3"
      data-testid="email-auth-form-login"
      onSubmit={handleSubmit((values) => onSubmit(values))}
    >
      <FormError message={errorMessage} />
      <div className="space-y-1.5">
        <Label htmlFor="email-auth-login-email">Email</Label>
        <Input
          id="email-auth-login-email"
          type="email"
          autoComplete="email"
          placeholder="you@example.com"
          {...emailField}
          onChange={(e) => {
            void emailField.onChange(e);
            onEmailChange(e.target.value);
          }}
        />
        <FieldError message={errors.email?.message} />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="email-auth-login-password">Password</Label>
        <Input
          id="email-auth-login-password"
          type="password"
          autoComplete="current-password"
          {...register('password')}
        />
        <FieldError message={errors.password?.message} />
      </div>
      <Button type="submit" size="lg" disabled={isSubmitting}>
        {isSubmitting ? 'Signing in…' : 'Sign in'}
      </Button>
    </form>
  );
}

function RegisterForm({
  onSubmit,
  isSubmitting,
  errorMessage,
  defaultEmail,
  onEmailChange,
}: FormProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<RegisterValues>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      email: defaultEmail,
      name: '',
      password: '',
      confirmPassword: '',
    },
  });
  const emailField = register('email');

  return (
    <form
      className="flex flex-col gap-3"
      data-testid="email-auth-form-register"
      onSubmit={handleSubmit((values) =>
        onSubmit({ email: values.email, password: values.password, name: values.name }),
      )}
    >
      <FormError message={errorMessage} />
      <div className="space-y-1.5">
        <Label htmlFor="email-auth-register-email">Email</Label>
        <Input
          id="email-auth-register-email"
          type="email"
          autoComplete="email"
          placeholder="you@example.com"
          {...emailField}
          onChange={(e) => {
            void emailField.onChange(e);
            onEmailChange(e.target.value);
          }}
        />
        <FieldError message={errors.email?.message} />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="email-auth-register-name">Name (optional)</Label>
        <Input
          id="email-auth-register-name"
          type="text"
          autoComplete="name"
          placeholder="Ada Lovelace"
          {...register('name')}
        />
        <FieldError message={errors.name?.message} />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="email-auth-register-password">Password</Label>
        <Input
          id="email-auth-register-password"
          type="password"
          autoComplete="new-password"
          {...register('password')}
        />
        <FieldError message={errors.password?.message} />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="email-auth-register-confirm">Confirm password</Label>
        <Input
          id="email-auth-register-confirm"
          type="password"
          autoComplete="new-password"
          {...register('confirmPassword')}
        />
        <FieldError message={errors.confirmPassword?.message} />
      </div>
      <Button type="submit" size="lg" disabled={isSubmitting}>
        {isSubmitting ? 'Creating account…' : 'Create account'}
      </Button>
    </form>
  );
}
